<template>
  <div
    class="CreditCardDisplay"
    @mouseenter="enableAnimation"
    @mousemove="tiltCard"
    @mouseleave="disableAnimation"
    @touchstart.passive="enableAnimation"
    @touchmove="tiltCardTouch"
    @touchend="disableAnimation"
    @touchcancel="disableAnimation"
  >
    <div class="CreditCardDisplay-title">
      {{ title }}
    </div>

    <div class="CreditCardDisplay-details">
      <span class="CreditCardDisplay-number">
        **** **** **** {{ last4 }}
      </span>
      <span v-if="hasExpiry" class="CreditCardDisplay-expiry">
        {{ String(expiryMonth).padStart(2, '0') }}/{{ String(expiryYear).slice(-2) }}
      </span>
      <span class="CreditCardDisplay-holder">
        {{ cardholderName || '' }}
      </span>
    </div>

    <div class="CreditCardDisplay-footer">
      <div v-if="creditCardBrandIconUrl" class="CreditCardDisplay-issuer">
        <Icon
          v-if="creditCardBrandIconUrl === 'other'"
          icon="credit-card"
          size="md"
          aria-label="Card brand"
        />
        <img
          v-else
          :src="creditCardBrandIconUrl"
          :alt="brand || 'Logo'"
          width="32"
          height="20"
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount } from 'vue';
import { normalize } from '@cvo/domain';
import { Icon } from '@cvo/ui/components';

export interface CreditCardDisplayProps {
  title: string;
  brand: string;
  creditCardBrandIconUrl?: string | null;
  last4: string;
  expiryMonth?: number;
  expiryYear?: number;
  cardholderName?: string;
}

const props = defineProps<CreditCardDisplayProps>();

const hasExpiry = computed(() => !!props.expiryMonth && !!props.expiryYear);

// ===== Card tilting and glare animation ===== //

const maxRotation = 10;
const transitionDelayMs = 200;

let disableTransitionTimeoutId: ReturnType<typeof setTimeout> | undefined;

function enableAnimation(event: MouseEvent | TouchEvent) {
  const target = event.currentTarget as HTMLDivElement;
  target.classList.add('is-tilting');
  clearTimeout(disableTransitionTimeoutId);
  disableTransitionTimeoutId = setTimeout(() => {
    target.style.transition = 'none';
  }, transitionDelayMs + 300);
}

function tiltCard(event: MouseEvent) {
  applyTilt(event.currentTarget as HTMLDivElement, event.clientX, event.clientY);
}

function tiltCardTouch(event: TouchEvent) {
  const touch = event.touches[0];
  if (!touch) return;
  event.preventDefault(); // stop the page from scrolling while tilting the card
  applyTilt(event.currentTarget as HTMLDivElement, touch.clientX, touch.clientY);
}

function applyTilt(target: HTMLDivElement, clientX: number, clientY: number) {
  const rect = target.getBoundingClientRect();
  const posX = clientX - rect.left;
  const posY = clientY - rect.top;
  const rotateY = normalize(posX, 0, rect.width, -maxRotation, maxRotation);
  const rotateX = normalize(posY, 0, rect.height, maxRotation, -maxRotation);

  // Without a perspective, rotateX/Y have no depth foreshortening and, since cos(θ) === cos(-θ),
  // opposite corners render identically — only box-shadow would appear to change.
  target.style.transform = `perspective(600px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  target.style.boxShadow = `${-rotateY}px ${rotateX}px 30px rgba(0,0,0,0.15)`;
  target.style.setProperty('--glare-x', `${normalize(posX, 0, rect.width, 0, 100)}%`);
  target.style.setProperty('--glare-y', `${normalize(posY, 0, rect.height, 0, 100)}%`);
}

function disableAnimation(event: MouseEvent | TouchEvent) {
  const target = event.currentTarget as HTMLDivElement;
  clearTimeout(disableTransitionTimeoutId);
  target.classList.remove('is-tilting');
  target.style.transform = '';
  target.style.boxShadow = '';
  target.style.transition = `box-shadow ${transitionDelayMs} ease-out, transform ${transitionDelayMs} ease-out`;
}

onBeforeUnmount(() => {
  clearTimeout(disableTransitionTimeoutId);
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .CreditCardDisplay {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-4;
    padding: vars.$dimensions-d-3;
    aspect-ratio: 85.6 / 54; // Credit card ratio
    // DS-331: gradient-color-1/2 are custom tokens, not part of the official design system yet.
    background:
      radial-gradient(120% 140% at 15% 20%, vars.$organisms-card-creditcarddisplay-gradient-color-1 0%, transparent 55%),
      radial-gradient(120% 140% at 85% 80%, vars.$organisms-card-creditcarddisplay-gradient-color-2 0%, transparent 55%),
      linear-gradient(135deg, #f5f6ff 0%, #eef1fb 100%);
    border-radius: vars.$border-radius-md; // TODO: missing variable in figma
    inline-size: 100%;
    max-width: 300px;
    min-width: 200px;
    overflow: hidden;

    @include responsive.mobile {
      width: 100%;
      min-width: auto;
    }

    &-title {
      color: vars.$organisms-card-contentcard-details-label-color;
      @include text.text('sm');
      font-weight: vars.$typography-weight-strong;
    }

    &-details {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
    }

    &-number,
    &-expiry,
    &-holder {
      color: vars.$organisms-card-contentcard-details-label-color;
      text-transform: capitalize;
      @include text.text('sm');
    }

    &-footer {
      align-self: flex-end;
      margin-top: auto;
    }

    &-issuer {
      display: flex;
      justify-content: flex-end;
      gap: vars.$dimensions-d-1;
      align-items: center;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(circle at var(--glare-x, 50%) var(--glare-y, 50%), rgba(255, 255, 255, 0.5), transparent 55%);
      opacity: 0;
      transition: opacity 300ms ease-out;
      pointer-events: none;
    }

    &.is-tilting::after {
      opacity: 1;
      transition: none;
    }
  }
}
</style>
